import React, { createContext } from 'react';
import { CommonFileManagerProps, DirectoryMapNode, DirectoryNode, PreviewImageItem, TabularFileItem } from 'types/CommonFileManager/commonFileManager';


// ==============================|| CONFIGURATION CONTEXT ||============================== //

export interface FileManagerConfigContextType extends Partial<CommonFileManagerProps> {
    isUploading?: boolean;
}

export const FileManagerConfigContext = createContext<FileManagerConfigContextType | undefined>(undefined);

export const FileManagerProvider: React.FC<FileManagerConfigContextType & { children: React.ReactNode }> = ({
    children,
    ...props
}) => {
    return (
        <FileManagerConfigContext.Provider value={props}>
            {children}
        </FileManagerConfigContext.Provider>
    );
};

// ==============================|| LOCAL STATE CONTEXT ||============================== //

export interface CommonFileManagerStateContextType {
    // States
    selectedDirectoryId: string;
    setSelectedDirectoryId: React.Dispatch<React.SetStateAction<string>>;
    isProcessing: boolean;
    setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>;
    processingText: string;
    setProcessingText: React.Dispatch<React.SetStateAction<string>>;
    isLoading: boolean;
    apiFilesLoading: boolean;
    directoryMap: Record<string, DirectoryMapNode>;
    setDirectoryMap: React.Dispatch<React.SetStateAction<Record<string, DirectoryMapNode>>>;
    filesMap: Record<string, TabularFileItem[]>;
    setFilesMap: React.Dispatch<React.SetStateAction<Record<string, TabularFileItem[]>>>;
    items: TabularFileItem[];
    setItems: React.Dispatch<React.SetStateAction<TabularFileItem[]>>;
    drawerOpen: boolean;
    setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
    treeData: DirectoryNode;
    directoryOptions: { id: string; name: string }[];
    container: HTMLDivElement | null;
    setContainer: (el: HTMLDivElement | null) => void;
    uploadTargetDirectoryId: string | null;
    setUploadTargetDirectoryId: React.Dispatch<React.SetStateAction<string | null>>;
    uploadingQueue: Array<{
        id: string;
        name: string;
        size: string;
        progress: number;
        status: 'queued' | 'uploading' | 'completed' | 'failed';
        targetDirId: string;
        file?: File;
    }>;
    setUploadingQueue: React.Dispatch<React.SetStateAction<Array<{
        id: string;
        name: string;
        size: string;
        progress: number;
        status: 'queued' | 'uploading' | 'completed' | 'failed';
        targetDirId: string;
        file?: File;
    }>>>;
    deletingItems: Record<string, { progress: number; status: 'deleting' | 'completed' }>;
    setDeletingItems: React.Dispatch<React.SetStateAction<Record<string, { progress: number; status: 'deleting' | 'completed' }>>>;

    // Modal States
    isAddDirectoryOpen: boolean;
    setIsAddDirectoryOpen: React.Dispatch<React.SetStateAction<boolean>>;
    addDirectoryParentId: string;
    setAddDirectoryParentId: React.Dispatch<React.SetStateAction<string>>;

    isRenameDirectoryOpen: boolean;
    setIsRenameDirectoryOpen: React.Dispatch<React.SetStateAction<boolean>>;
    renameDirectoryItem: TabularFileItem | null;
    setRenameDirectoryItem: React.Dispatch<React.SetStateAction<TabularFileItem | null>>;

    isRenameFileOpen: boolean;
    setIsRenameFileOpen: React.Dispatch<React.SetStateAction<boolean>>;
    renameFileItem: TabularFileItem | null;
    setRenameFileItem: React.Dispatch<React.SetStateAction<TabularFileItem | null>>;

    isNoteOpen: boolean;
    setIsNoteOpen: React.Dispatch<React.SetStateAction<boolean>>;
    activeNoteItem: TabularFileItem | null;
    setActiveNoteItem: React.Dispatch<React.SetStateAction<TabularFileItem | null>>;

    isMoveOpen: boolean;
    setIsMoveOpen: React.Dispatch<React.SetStateAction<boolean>>;
    movingItemIds: string[];
    setMovingItemIds: React.Dispatch<React.SetStateAction<string[]>>;

    imageEditorOpen: boolean;
    setImageEditorOpen: React.Dispatch<React.SetStateAction<boolean>>;
    fileForEditor: TabularFileItem | null;
    setFileForEditor: React.Dispatch<React.SetStateAction<TabularFileItem | null>>;
    imageUrlsMap: Record<string, string>;
    setImageUrlsMap: React.Dispatch<React.SetStateAction<Record<string, string>>>;

    previewImages: PreviewImageItem[];
    setPreviewImages: React.Dispatch<React.SetStateAction<PreviewImageItem[]>>;
    previewIndex: number;
    setPreviewIndex: React.Dispatch<React.SetStateAction<number>>;

    isDeleteOpen: boolean;
    setIsDeleteOpen: React.Dispatch<React.SetStateAction<boolean>>;
    deleteConfig: {
        title: string;
        subtitle: string;
        itemName: string;
        itemDescription: string;
        itemType: 'folder' | 'file' | 'mixed';
        showCard?: boolean;
        isFileDelete?: boolean;
        fileName?: string;
        filePath?: string;
        isMultiple?: boolean;
        filesCount?: number;
        confirmAction: () => void;
    } | null;
    setDeleteConfig: React.Dispatch<React.SetStateAction<any>>;

    // Refs
    fileInputRef: React.RefObject<HTMLInputElement | null>;

    // Handlers
    selectDirectory: (directoryId: string) => void;
    handleDirectorySelect: (node: DirectoryNode) => void;
    handleDirectoryDoubleClick: (node: DirectoryNode) => void;
    handleBreadcrumbClick: (index: number) => void;
    handleBackClick: () => void;
    handleDirectoryClick: (directory: TabularFileItem) => void;
    handleUploadClick: () => void;
    handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
    handleNewDirectoryClick: () => void;
    handleNewDirectoryFromTree: (parentDir: DirectoryNode) => void;
    handleAddDirectorySubmit: (parentId: string, folderName: string) => Promise<void>;
    handleEditDirectory: (directory: TabularFileItem) => void;
    handleRenameDirectorySubmit: (directoryId: string, newName: string) => Promise<void>;
    handleEditFileRename: (file: TabularFileItem) => void;
    handleRenameFileSubmit: (fileId: string, newName: string) => Promise<void>;
    handleDeleteDirectory: (directory: TabularFileItem) => void;
    handleFileClick: (file: TabularFileItem) => void;
    handleEditFile: (file: TabularFileItem) => void;
    handleImageEditorSave: (savedImageData: any, fileName: string, isSaveAsNew?: boolean) => Promise<void>;
    handleDownloadFile: (file: TabularFileItem, customRelativePath?: string, skipProcessingState?: boolean) => Promise<void>;
    handleDeleteFile: (file: TabularFileItem) => void;
    handleDeleteSelected: (ids: string[]) => void;
    handleBulkDownload: (ids: string[]) => Promise<void>;
    handleDownloadIndividual?: (ids: string[]) => Promise<void>;
    handleAddNote: (file: TabularFileItem) => void;
    handleNoteSubmit: (fileId: string, note: string) => void;
    handleMoveSubmit: (targetDirectoryId: string) => Promise<boolean>;
    checkDuplicateName: (parentId: string, nameToCheck: string, excludeId?: string) => boolean;
    handleRetryUpload?: (id: string) => Promise<void>;
    handleCancelUpload?: (id: string) => void;
    allowAdd: boolean;
    allowDelete: boolean;
    allowEdit: boolean;
    canManipulateItem: (item: TabularFileItem) => boolean;
}

export const CommonFileManagerStateContext = createContext<CommonFileManagerStateContextType | undefined>(undefined);
